/**
 * Central Analytics Provider Configuration
 * Control which providers are enabled/disabled for the entire application
 */

export interface ProviderEnableConfig {
  posthog: boolean;
  moengage: boolean;
  // Add future providers here
  [key: string]: boolean;
}

export interface AnalyticsProviderConfig {
  providers: ProviderEnableConfig;
}

/**
 * Default provider configuration
 * Set to false to completely disable a provider
 */
export const DEFAULT_PROVIDER_CONFIG: AnalyticsProviderConfig = {
  providers: {
    posthog: false,      // Set to false to disable PostHog
    moengage: true,     // Set to false to disable MoEngage
  }
};

/**
 * Environment-specific provider configuration
 * Override DEFAULT_PROVIDER_CONFIG based on environment
 */
export function getEnvironmentProviderConfig(): AnalyticsProviderConfig {
  const config = { ...DEFAULT_PROVIDER_CONFIG };
  
  // Check if we're in development mode
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    console.log('[AnalyticsProviderConfig] Development mode detected');
  }
  
  return config;
}

/**
 * Check if a specific provider is enabled
 */
export function isProviderEnabled(providerName: string): boolean {
  const config = getEnvironmentProviderConfig();
  return config.providers[providerName] === true;
}

/**
 * Check if analytics is globally enabled
 */
export function isAnalyticsEnabled(): boolean {
  return true; // Always enabled unless disabled via localStorage
}

/**
 * Get all enabled provider names
 */
export function getEnabledProviders(): string[] {
  const config = getEnvironmentProviderConfig();
  return Object.entries(config.providers)
    .filter(([_, enabled]) => enabled)
    .map(([name, _]) => name);
}

/**
 * Runtime provider control functions
 */
export class ProviderControl {
  /**
   * Disable a specific provider at runtime
   */
  static disableProvider(providerName: string): void {
    const config = getEnvironmentProviderConfig();
    if (config.providers[providerName] !== undefined) {
      config.providers[providerName] = false;
      console.log(`[AnalyticsProviderConfig] Provider '${providerName}' disabled`);
    }
  }
  
  /**
   * Enable a specific provider at runtime
   */
  static enableProvider(providerName: string): void {
    const config = getEnvironmentProviderConfig();
    if (config.providers[providerName] !== undefined) {
      config.providers[providerName] = true;
      console.log(`[AnalyticsProviderConfig] Provider '${providerName}' enabled`);
    }
  }
  
  /**
   * Get current provider status
   */
  static getProviderStatus(): Record<string, boolean> {
    const config = getEnvironmentProviderConfig();
    return {
      ...config.providers
    };
  }
} 